import { useCallback, useEffect, useMemo, useState } from 'react';
import { Location } from '../../domain/location';
import {
  recordKey,
  scopedRecordCollectionId,
} from '../collection/multiCollectionIdentity';

export interface ItemFilterValues {
  locations: string[];
  tags: string[];
  description: string;
  locationName: string;
}
export interface LocationFilterScope {
  collectionIds: string[];
  unavailableCollectionIds: string[];
  completeCollectionIds: string[];
  pending?: boolean;
}

export function retainAvailableLocationKeys(
  keys: string[],
  locations: Location[],
  scope?: LocationFilterScope,
): string[] {
  return retainAvailableFilterKeys(keys, locations.map(recordKey), scope);
}

export function retainAvailableFilterKeys(
  keys: string[],
  availableKeys: string[],
  scope?: LocationFilterScope,
): string[] {
  if (!scope) return keys;
  const available = new Set(availableKeys);
  const remaining = keys.filter((key) => {
    const collectionId = scopedRecordCollectionId(key);
    return (
      !collectionId ||
      ((scope.pending || scope.collectionIds.includes(collectionId)) &&
        !scope.unavailableCollectionIds.includes(collectionId) &&
        (!scope.completeCollectionIds.includes(collectionId) ||
          available.has(key)))
    );
  });
  return remaining.length === keys.length ? keys : remaining;
}

const emptyFilters = (): ItemFilterValues => ({
  locations: [],
  tags: [],
  description: '',
  locationName: '',
});
const storageKey = (userId: string) => `qkindex.itemFilters.${userId}`;
function readFilters(userId: string): ItemFilterValues {
  try {
    const saved = JSON.parse(
      localStorage.getItem(storageKey(userId)) || 'null',
    );
    if (
      saved?.version === 1 &&
      Array.isArray(saved.locations) &&
      saved.locations.every(
        (key: unknown) => typeof key === 'string' && key.length > 0,
      ) &&
      typeof saved.description === 'string'
    ) {
      const tags =
        Array.isArray(saved.tags) &&
        saved.tags.every(
          (key: unknown) => typeof key === 'string' && key.length > 0,
        )
          ? [...new Set<string>(saved.tags)]
          : [];
      return {
        locations: [...new Set<string>(saved.locations)],
        tags,
        description: saved.description,
        locationName:
          typeof saved.locationName === 'string' ? saved.locationName : '',
      };
    }
  } catch {
    /* Optional local preference. */
  }
  return emptyFilters();
}

/** Only applied filters persist. User changes never carry another account's filters. */
export function useItemFilters(userId: string, persist = true) {
  const initial = useMemo(
    () => (persist ? readFilters(userId) : emptyFilters()),
    [userId, persist],
  );
  const [state, setState] = useState(() => ({ userId, value: initial }));
  const filters = state.userId === userId ? state.value : initial;
  useEffect(() => {
    if (!persist) return;
    try {
      localStorage.setItem(
        storageKey(userId),
        JSON.stringify({ version: 1, ...filters }),
      );
    } catch {
      /* Browsing still works when storage is unavailable. */
    }
  }, [userId, filters, persist]);
  const setFilters = useCallback(
    (
      update:
        ItemFilterValues | ((previous: ItemFilterValues) => ItemFilterValues),
    ) => {
      setState((previous) => {
        const current = previous.userId === userId ? previous.value : initial;
        const value = typeof update === 'function' ? update(current) : update;
        return previous.userId === userId && value === current
          ? previous
          : { userId, value };
      });
    },
    [userId, initial],
  );
  return [filters, setFilters] as const;
}
